import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, ArrowLeft } from "lucide-react";

import { useAuth } from "@/context/AuthContext";
import { trackEvent } from "@/utils/analytics";
import { Logo } from "@/components/Logo";
import { spring, useUITheme, ThemeToggle, pageTheme } from "@/components/ios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling");
  const successTracked = useRef(false);
  const { user } = useAuth();
  const { dark, toggle } = useUITheme();
  const t = pageTheme(dark);

  const primaryBtn = `inline-flex items-center justify-center gap-2 min-h-[48px] px-6 rounded-full ${t.primaryBtn} text-[15px] font-semibold active:scale-[0.98] transition-transform duration-150`;
  const secondaryBtn = `inline-flex items-center justify-center gap-2 min-h-[48px] px-6 rounded-full ${t.secondaryBtn} text-[15px] font-semibold active:scale-[0.98] transition-transform duration-150`;

  useEffect(() => {
    if (!sessionId) {
      setStatus("missing");
      return;
    }
    let attempts = 0;
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          if (!successTracked.current) {
            successTracked.current = true;
            trackEvent("checkout_success", { plan: res.data.lookup_key, source: "stripe", session_id: sessionId });
          }
          setStatus("paid");
          return;
        }
      } catch {}
      attempts += 1;
      if (attempts < 15) setTimeout(poll, 2000);
      else setStatus("timeout");
    };
    poll();
  }, [sessionId]);

  return (
    <main className={`min-h-screen flex items-center justify-center px-5 py-10 font-sans ${t.page}`} data-testid="payment-success-page" data-theme={dark ? "dark" : "light"}>
      <div className="fixed top-5 right-5 z-40">
        <ThemeToggle dark={dark} onToggle={toggle} testid="payment-theme-toggle" />
      </div>
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={spring}
        className={`rounded-[32px] p-8 md:p-12 max-w-lg w-full text-center ${t.card}`}
      >
        <div className="flex justify-center mb-8"><Logo /></div>
        {status === "paid" ? (
          <>
            <CheckCircle2 className="w-12 h-12 text-[#34C759] mx-auto mb-5" strokeWidth={1.5} />
            <h1 className={`font-bold tracking-tight text-3xl md:text-4xl mb-3 ${t.primary}`} data-testid="payment-success-title">Deal closed.</h1>
            <p className={`text-[15px] ${t.secondary} leading-relaxed mb-8`}>
              Your Doclyx subscription is active. {user ? "Your workspace is unlocked — every engine is yours." : "Create your workspace login with the same email you used at checkout."}
            </p>
            {user ? (
              <Link to="/workspace" data-testid="open-workspace-link" className={`${primaryBtn} mb-4`}>
                Open your workspace
              </Link>
            ) : (
              <span className="flex flex-col sm:flex-row justify-center gap-3 mb-4">
                <Link to="/login?mode=signup&next=/workspace" data-testid="create-workspace-login-link" className={primaryBtn}>
                  Create workspace login
                </Link>
                <Link to="/login?next=/workspace" data-testid="signin-workspace-link" className={secondaryBtn}>
                  Sign in
                </Link>
              </span>
            )}
            <br />
          </>
        ) : status === "polling" ? (
          <>
            <Loader2 className={`w-9 h-9 ${t.link} mx-auto mb-5 animate-spin`} strokeWidth={1.5} />
            <h1 className={`font-bold tracking-tight text-2xl mb-3 ${t.primary}`}>Confirming payment…</h1>
            <p className={`text-[15px] ${t.secondary}`}>Hold on while we verify with Stripe.</p>
          </>
        ) : status === "missing" ? (
          <>
            <h1 className={`font-bold tracking-tight text-2xl mb-3 ${t.primary}`} data-testid="payment-missing-title">No checkout session found.</h1>
            <p className={`text-[15px] ${t.secondary} leading-relaxed mb-8`}>
              Choose a plan first — once checkout completes, your workspace access will activate here.
            </p>
            <Link to="/#pricing" data-testid="missing-session-pricing-link" className={`${primaryBtn} mb-4`}>
              Choose a plan
            </Link>
            <br />
          </>
        ) : (
          <>
            <h1 className={`font-bold tracking-tight text-2xl mb-3 ${t.primary}`}>Payment received.</h1>
            <p className={`text-[15px] ${t.secondary} leading-relaxed mb-8`}>
              Confirmation is taking longer than usual — your access will activate automatically.
            </p>
          </>
        )}
        <Link to="/" data-testid="back-home-link" className={secondaryBtn}>
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Back to Doclyx
        </Link>
      </motion.div>
    </main>
  );
}
